import {
  Row,
  Col,
  Typography,
  Space,
  Spin,
  Divider,
  Breadcrumb,
  Card,
  Tag,
  Alert,
  Button,
  Rate,
  Tabs,
} from "antd";
import React, { useState, useEffect } from "react";
import { HomeOutlined } from "@ant-design/icons";
import { api } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AvatarDisplay from "../Dashboard/Account/AvatarDisplay";
import { formatPolishCurrency } from "../../helpers/currency";
import { getAuthToken, isLogged } from "../../helpers/account";
import NewTransactionForm from "../Transaction/Forms/NewTransactionForm";
import AccountTags from "../Dashboard/Account/AccountTags";

const { Title, Text, Paragraph } = Typography;

const MarketplaceAccountProfilePage = () => {
  const { t } = useTranslation();
  const { accountUuid } = useParams(); // Capture the UUID from the route
  const [account, setAccount] = useState(null);
  const [initiatorRatings, setInitiatorRatings] = useState([]);
  const [counterpartyRatings, setCounterpartyRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRatings, setLoadingRatings] = useState(true);

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const response = await fetch(
          `${api.host}/accounts/${accountUuid}/public`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch account details");
        }
        const data = await response.json();
        setAccount(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching account data:", error);
        setLoading(false);
      }
    };

    fetchAccountData();
  }, [accountUuid]);

  // Fetch user ratings
  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const [initiatorResponse, counterpartyResponse] = await Promise.all([
          fetch(`${api.host}/transactions/ratings/${accountUuid}/initiator`, {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
              ...(isLogged() && { Authorization: `${getAuthToken()}` }),
            }),
          }),
          fetch(`${api.host}/transactions/ratings/${accountUuid}/counterparty`, {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
              ...(isLogged() && { Authorization: `${getAuthToken()}` }),
            }),
          })
        ]);

        if (initiatorResponse.ok) {
          const initiatorData = await initiatorResponse.json();
          setInitiatorRatings(initiatorData);
        }

        if (counterpartyResponse.ok) {
          const counterpartyData = await counterpartyResponse.json();
          setCounterpartyRatings(counterpartyData);
        }

        setLoadingRatings(false);
      } catch (error) {
        console.error("Error fetching ratings:", error);
        setLoadingRatings(false);
      }
    };

    fetchRatings();
  }, [accountUuid]);

  if (loading) {
    return (
      <Spin spinning={loading}>
        <div style={{ textAlign: "center", margin: "20px" }}>
          {t('profile.loading')}
        </div>
      </Spin>
    );
  }

  return (
    <>
      <Helmet>
        <title>{account?.company_name || account?.fullname || ""} - {t('profile.companyProfile')}</title>
      </Helmet>

      <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "40px 20px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* Hero Section */}
          <div style={{ 
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", 
            borderRadius: "12px", 
            padding: "60px 40px", 
            textAlign: "center", 
            color: "white",
            marginBottom: "40px"
          }}>
            <AvatarDisplay src={account?.photo} account={account} size={120} />
            <Title level={1} style={{ color: "white", marginTop: "20px", marginBottom: "8px" }}>
              {account?.fullname || account?.company_name}
            </Title>
            {account?.company_name && account?.fullname && (
              <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: "18px" }}>
                {account.company_name}
              </Text>
            )}
            
            <div style={{ marginTop: "30px" }}>
              {isLogged() ? (
                <NewTransactionForm
                  initiatorTransactionRoleId={{
                    value: 2,
                    label: t('profile.buyer'),
                  }}
                  email={account.email}
                />
              ) : (
                <Link to="/transactions">
                  <Button type="primary" size="large" style={{ 
                    height: "56px", 
                    fontSize: "18px", 
                    fontWeight: "600",
                    padding: "0 40px"
                  }}>
                    {t('profile.assignTask')}
                  </Button>
                </Link>
              )}
            </div>
          </div>

          <Row gutter={[32, 32]}>
            {/* Left Column - Profile Info */}
            <Col xs={24} lg={16}>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* About Section */}
                <Card 
                  title={t('profile.aboutMe')}
                  style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                >
                  <Paragraph style={{ fontSize: "16px", lineHeight: "1.6" }}>
                    {account?.description || 
                      t('profile.defaultDescription')
                    }
                  </Paragraph>
                  {account?.tags?.length > 0 && (
                    <div style={{ marginTop: "20px" }}>
                      <Text strong style={{ marginBottom: "12px", display: "block" }}>{t('profile.skills')}:</Text>
                      <AccountTags tags={account.tags} />
                    </div>
                  )}
                </Card>

                {/* Ratings Section */}
                <Card 
                  title={t('profile.userReviews')}
                  style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                >
                  <Spin spinning={loadingRatings}>
                    <Tabs
                      items={[
                        {
                          key: "initiator",
                          label: `${t('profile.asClient')} (${initiatorRatings.length})`,
                          children: initiatorRatings.length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                              {initiatorRatings.map((rating, index) => (
                                <div key={index} style={{ 
                                  padding: "16px", 
                                  background: "#f8f9fa", 
                                  borderRadius: "8px",
                                  border: "1px solid #e9ecef"
                                }}>
                                  <div style={{ marginBottom: "12px" }}>
                                    <Text strong style={{ color: "#2c3e50", fontSize: "15px", display: "block", marginBottom: "8px" }}>
                                      {rating.title}
                                    </Text>
                                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                      <Rate disabled value={rating.rating} style={{ fontSize: "16px" }} />
                                      <Text style={{ color: "#6c757d", fontSize: "14px" }}>
                                        {new Date(rating.created_at).toLocaleDateString('pl-PL')}
                                      </Text>
                                    </div>
                                  </div>
                                  {rating.comment && (
                                    <Text style={{ color: "#2c3e50" }}>{rating.comment}</Text>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ textAlign: "center", padding: "40px 20px" }}>
                              <div style={{ fontSize: "48px", marginBottom: "16px" }}>⭐</div>
                              <Title level={4} style={{ color: "#6c757d", marginBottom: "8px" }}>
                                {t('profile.noReviews')}
                              </Title>
                              <Text style={{ color: "#6c757d" }}>
                                {t('profile.noReviewsAsClient')}
                              </Text>
                            </div>
                          )
                        },
                        {
                          key: "counterparty",
                          label: `${t('profile.asContractor')} (${counterpartyRatings.length})`,
                          children: counterpartyRatings.length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                              {counterpartyRatings.map((rating, index) => (
                                <div key={index} style={{ 
                                  padding: "16px", 
                                  background: "#f8f9fa", 
                                  borderRadius: "8px",
                                  border: "1px solid #e9ecef"
                                }}>
                                  <div style={{ marginBottom: "12px" }}>
                                    <Text strong style={{ color: "#2c3e50", fontSize: "15px", display: "block", marginBottom: "8px" }}>
                                      {rating.title}
                                    </Text>
                                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                      <Rate disabled value={rating.rating} style={{ fontSize: "16px" }} />
                                      <Text style={{ color: "#6c757d", fontSize: "14px" }}>
                                        {new Date(rating.created_at).toLocaleDateString('pl-PL')}
                                      </Text>
                                    </div>
                                  </div>
                                  {rating.comment && (
                                    <Text style={{ color: "#2c3e50" }}>{rating.comment}</Text>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ textAlign: "center", padding: "40px 20px" }}>
                              <div style={{ fontSize: "48px", marginBottom: "16px" }}>⭐</div>
                              <Title level={4} style={{ color: "#6c757d", marginBottom: "8px" }}>
                                {t('profile.noReviews')}
                              </Title>
                              <Text style={{ color: "#6c757d" }}>
                                {t('profile.noReviewsAsContractor')}
                              </Text>
                            </div>
                          )
                        }
                      ]}
                    />
                  </Spin>
                </Card>
              </Space>
            </Col>

            {/* Right Column - Quick Info */}
            <Col xs={24} lg={8}>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* Quick Stats */}
                <Card 
                  title={t('profile.statistics')}
                  style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <Text>{t('profile.asClient')}:</Text>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Rate 
                          disabled 
                          value={initiatorRatings.length > 0 ? initiatorRatings.reduce((sum, r) => sum + r.rating, 0) / initiatorRatings.length : 0} 
                          allowHalf 
                          style={{ fontSize: "14px" }}
                        />
                        <Text strong>
                          {initiatorRatings.length > 0 ? (initiatorRatings.reduce((sum, r) => sum + r.rating, 0) / initiatorRatings.length).toFixed(1) : "0.0"}
                        </Text>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <Text>{t('profile.asContractor')}:</Text>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Rate 
                          disabled 
                          value={counterpartyRatings.length > 0 ? counterpartyRatings.reduce((sum, r) => sum + r.rating, 0) / counterpartyRatings.length : 0} 
                          allowHalf 
                          style={{ fontSize: "14px" }}
                        />
                        <Text strong>
                          {counterpartyRatings.length > 0 ? (counterpartyRatings.reduce((sum, r) => sum + r.rating, 0) / counterpartyRatings.length).toFixed(1) : "0.0"}
                        </Text>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <Text>{t('profile.totalReviews')}:</Text>
                      <Text strong>{initiatorRatings.length + counterpartyRatings.length}</Text>
                    </div>
                  </Space>
                </Card>

                {/* Help Card */}
                <Card 
                  title={`💡 ${t('profile.tip')}`}
                  style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", background: "#f8f9fa" }}
                >
                  <Text style={{ color: "#6c757d", fontSize: "14px", lineHeight: "1.6" }}>
                    {t('profile.tipText')}
                  </Text>
                </Card>
              </Space>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default MarketplaceAccountProfilePage;
